import TextArea from "../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../utils/Common/Constant";
import { useState } from "react";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import Button from "Components/Common/Button/Button";
import { lenderDrawAction } from "services/lenderService";
import { useToast } from "Components/Common/Toast/ToastContext";

interface DrawActionModalProps {
  projectId: string;
  drawId: string;
  drawNumber: number;
  action: "approved" | "rejected" | null;
  onClose: () => void;
  onSuccess: (action: "approved" | "rejected") => void;
}

export default function DrawActionModal({
  projectId,
  drawId,
  drawNumber,
  action,
  onClose,
  onSuccess,
}: DrawActionModalProps) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const showToast = useToast();

  const handleClose = () => {
    setNote("");
    onClose();
  };

  const handleConfirm = async () => {
    if (!action) return;
    if (action === "rejected" && !note.trim()) {
      showToast("Please provide a reason for rejection.");
      return;
    }
    setSubmitting(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await (lenderDrawAction(projectId, drawId, {
        action,
        lenderNote: note,
      }) as Promise<any>);
      if (res.error) {
        showToast(res.error.message || `Failed to ${action} draw.`);
      } else {
        showToast(`Draw ${action} successfully.`);
        handleClose();
        onSuccess(action);
      }
    } catch {
      showToast(`Failed to ${action} draw.`);
    } finally {
      setSubmitting(false);
    }
  };

  const drawLabel = `#${String(drawNumber).padStart(2, "0")}`;

  return (
    <CustomModal
      show={action !== null}
      handleClose={handleClose}
      isHeaderVisible
      isCrossBtn={false}
      isFooterVisible
      title={
        action === "approved"
          ? `Approve Draw ${drawLabel}?`
          : `Reject Draw ${drawLabel}?`
      }
      footerContent={
        <>
          <Button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className={action === "approved" ? "approve-btn" : "reject-btn"}
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting
              ? "Submitting…"
              : action === "approved"
                ? "Confirm Approval"
                : "Confirm Rejection"}
          </Button>
        </>
      }
    >
      <p>
        {action === "approved"
          ? `You are about to approve Draw ${drawLabel}. Add an optional note for the record:`
          : `Provide a reason for rejecting Draw ${drawLabel}. Your note will be shared with CSC New England and the GC.`}
      </p>
      <TextArea
        className="form-control mt-3"
        placeholder={
          action === "approved"
            ? "e.g. Approved pending GC clarification on electrical line item…"
            : "e.g. Holding pending resolution of overbilling flag…"
        }
        rows={4}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={submitting}
              maxChars={CHAR_LIMIT.DESCRIPTION}
            />
    </CustomModal>
  );
}
